import { Badge, Button, buttonClass, cardClass, fieldClass, Pagination, SearchField, selectClass, Table } from '@ledgerloom/ui';
import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api, propose } from '../api';
import { useApi, useDebounced, usePageTitle } from '../hooks';
import { useSession } from '../session';
import { Tabs } from '../components/Tabs';
import { Field } from '../components/Field';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { CardBoard, changedCards, type KanbanCard } from '../components/CardBoard';
import { Avatar, Empty, ErrorBanner, Icon, ListSkeleton, Time, ToggleGroup, outcomeMessage, outcomeTone, useToast } from '../components/ui';
import { className, defaultClass, gradeName, NO_STAGE, stageChoices, stageName, type Fields } from '../contactSettings';

// Contacts as views (docs/design/contacts.md): built-in and saved views across
// the top, each a filter, columns, a sort, and a layout; the list is a table
// with pages, the board a column per stage of one lifecycle. Patterns:
// navigation/tabs, data-display/table, data-display/kanban-board,
// data-display/filter-panel, forms/search-field, navigation/pagination.

interface Cond { field: string; op: string; value?: unknown }
interface Sort { field: string; dir: 'asc' | 'desc' }
interface View { id: string; name: string; builtin: boolean; filter: Cond[]; columns: string[]; sort: Sort; layout: 'list' | 'board'; lifecycle: string | null }
interface SavedView { id: string; name: string; filter_tree: { all?: Cond[] }; columns: string[]; sort: Sort; layout: 'list' | 'board'; lifecycle: string | null; updated_at: string }
interface Row { id: string; kind: string; display_name: string; version: number; contact_class: string; lifecycle_stage: string | null; values: Record<string, any> }
interface Card { id: string; display_name: string; version: number; contact_class: string; lifecycle_stage: string | null; email: string | null; owner_user_id: string | null; owner_name: string | null }
interface Column { stage: string | null; count: number; cards: Card[] }
interface User { id: string; display_name: string; status: string }

const OP_LABEL: Record<string, string> = {
  eq: 'is', neq: 'is not', contains: 'contains', exists: 'is set', not_exists: 'is not set', within_days: 'in the last', older_than_days: 'more than', in: 'is any of',
};
const NO_VALUE = new Set(['exists', 'not_exists']);
const HIDDEN = 'Hidden contact';
const sameView = (a: View, b: View) => JSON.stringify([a.filter, a.columns, a.sort, a.layout, a.lifecycle]) === JSON.stringify([b.filter, b.columns, b.sort, b.layout, b.lifecycle]);

export function Contacts() {
  usePageTitle('Contacts');
  const { me } = useSession();
  const toast = useToast();
  const [params, setParams] = useSearchParams();
  const fields = useApi<Fields>('/v1/contact-fields');
  const saved = useApi<SavedView[]>('/v1/contact-views');
  const users = useApi<User[]>('/v1/users');
  const [q, setQ] = useState(params.get('q') ?? '');
  const query = useDebounced(q);
  const status = params.get('status') ?? 'active';
  const page = Math.max(Number(params.get('page') ?? 1) || 1, 1);
  const perPage = [25, 50, 100].includes(Number(params.get('per'))) ? Number(params.get('per')) : 25;
  const set = (patch: Record<string, string | null>) => {
    const p = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) if (v === null) p.delete(k); else p.set(k, v);
    setParams(p, { replace: true });
  };
  useEffect(() => { if ((params.get('q') ?? '') !== query) set({ q: query || null, page: null }); }, [query]); // eslint-disable-line react-hooks/exhaustive-deps

  const defaults = fields.data?.default_columns ?? ['email', 'phone', 'owner', 'lifecycle_stage', 'last_activity'];
  const views: View[] = useMemo(() => [
    { id: 'all', name: 'All contacts', builtin: true, filter: [], columns: defaults, sort: { field: 'name', dir: 'asc' }, layout: 'list', lifecycle: null },
    { id: 'mine', name: 'My contacts', builtin: true, filter: [{ field: 'owner_user_id', op: 'eq', value: me.user_id }], columns: defaults, sort: { field: 'name', dir: 'asc' }, layout: 'list', lifecycle: null },
    { id: 'unassigned', name: 'Unassigned', builtin: true, filter: [{ field: 'owner_user_id', op: 'not_exists' }], columns: defaults, sort: { field: 'name', dir: 'asc' }, layout: 'list', lifecycle: null },
    ...(saved.data ?? []).map((v): View => ({ id: v.id, name: v.name, builtin: false, filter: v.filter_tree.all ?? [], columns: v.columns, sort: v.sort, layout: v.layout, lifecycle: v.lifecycle })),
  ], [saved.data, me.user_id, defaults.join()]); // eslint-disable-line react-hooks/exhaustive-deps
  const viewId = views.some((v) => v.id === params.get('view')) ? params.get('view')! : 'all';
  const base = views.find((v) => v.id === viewId)!;
  // The view as changed on screen; a tag link from a contact adds its condition.
  const [draft, setDraft] = useState<View>(base);
  useEffect(() => {
    const tag = params.get('tag');
    setDraft({ ...base, filter: tag ? [...base.filter, { field: 'tag', op: 'eq', value: tag }] : base.filter });
  }, [base.id, saved.data, fields.data]); // eslint-disable-line react-hooks/exhaustive-deps
  const dirty = !sameView(draft, base);
  const change = (patch: Partial<View>) => { setDraft((d) => ({ ...d, ...patch })); if (page !== 1) set({ page: null }); };

  const [panel, setPanel] = useState<'filter' | 'columns' | null>(null);
  const [naming, setNaming] = useState<'new' | 'rename' | null>(null);
  const [name, setName] = useState('');
  const [removing, setRemoving] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const body = { q: query || undefined, filter_tree: { all: draft.filter.filter((c) => NO_VALUE.has(c.op) || (c.value !== '' && c.value !== undefined)) }, status };
  const saveView = async (mode: 'new' | 'update' | 'rename') => {
    setBusy(true); setError(null);
    try {
      const v = { name: name.trim(), filter_tree: body.filter_tree, columns: draft.columns, sort: draft.sort, layout: draft.layout, lifecycle: draft.layout === 'board' ? draft.lifecycle : null };
      if (mode === 'new') {
        const out = await api<SavedView>('/v1/contact-views', { body: v });
        await saved.reload(); set({ view: out.id, tag: null }); toast(`Saved the view ${out.name}`);
      } else {
        await api(`/v1/contact-views/${base.id}`, { method: 'PUT', body: mode === 'rename' ? { name: v.name } : { ...v, name: undefined } });
        await saved.reload(); set({ tag: null }); toast(mode === 'rename' ? 'View renamed' : 'View saved');
      }
      setNaming(null);
    } catch (e) { setError(e); } finally { setBusy(false); }
  };
  const removeView = async () => {
    setBusy(true); setError(null);
    try { await api(`/v1/contact-views/${base.id}/archive`, { method: 'POST' }); await saved.reload(); set({ view: null }); toast('View removed'); setRemoving(false); }
    catch (e) { setError(e); setRemoving(false); } finally { setBusy(false); }
  };

  const tabs = views.map((v) => [v.id, v.name] as [string, string]);
  return (
    <div className="stack">
      <div className="page-head">
        <div><h1>Contacts</h1><p>People, households, and organizations.</p></div>
        <div className="row">
          <Link className={buttonClass({ variant: 'secondary' })} to="/import"><Icon name="upload" />Import</Link>
          <Link className={buttonClass()} to="/contacts/new"><Icon name="plus" />New contact</Link>
        </div>
      </div>
      <ErrorBanner error={fields.error ?? saved.error} />
      <div className="views-bar">
        <Tabs label="Views" value={viewId} tabs={tabs} onChange={(v) => set({ view: v === 'all' ? null : v, page: null, tag: null })}>
          <div className="stack" style={{ gap: 12 }}>
            <div className="contacts-toolbar">
              <div role="search" className="contacts-search">
                <SearchField id="contact-search" label="Search contacts" placeholder="Search by name, email, or phone" value={q} onValueChange={setQ} />
              </div>
              <Button variant="secondary" size="sm" className={panel === 'filter' ? 'border-accent bg-accent-subtle' : undefined} aria-expanded={panel === 'filter'} aria-controls="contacts-filter" onClick={() => setPanel(panel === 'filter' ? null : 'filter')}>
                <Icon name="filter" />Filter{draft.filter.length ? ` (${draft.filter.length})` : ''}
              </Button>
              <SortControl fields={fields.data} sort={draft.sort} onChange={(sort) => change({ sort })} />
              {draft.layout === 'list' && (
                <Button variant="secondary" size="sm" className={panel === 'columns' ? 'border-accent bg-accent-subtle' : undefined} aria-expanded={panel === 'columns'} aria-controls="contacts-columns" onClick={() => setPanel(panel === 'columns' ? null : 'columns')}>
                  <Icon name="columns" />Columns
                </Button>
              )}
              <ToggleGroup label="Show as" value={draft.layout} options={[['list', 'List'], ['board', 'Board']]} onChange={(layout) => change({ layout })} />
              {draft.layout === 'board' && (fields.data?.lifecycles.length ?? 0) > 1 && (
                <div className="row" style={{ gap: 4, flexWrap: 'nowrap' }}>
                  <label className="small muted" htmlFor="board-lifecycle">Lifecycle</label>
                  <select id="board-lifecycle" className={`${selectClass} h-9 w-auto`} value={draft.lifecycle ?? defaultClass(fields.data)?.lifecycle ?? ''}
                    onChange={(e) => change({ lifecycle: e.target.value === defaultClass(fields.data)?.lifecycle ? null : e.target.value })}>
                    {fields.data!.lifecycles.map((l) => <option key={l.key} value={l.key}>{l.name}</option>)}
                  </select>
                </div>
              )}
              <div className="row view-actions">
                {dirty && <Button type="button" variant="ghost" size="sm" onClick={() => { setDraft(base); set({ tag: null }); }}>Reset</Button>}
                {dirty && !base.builtin && <Button type="button" size="sm" disabled={busy} onClick={() => void saveView('update')}>Save view</Button>}
                {dirty && <Button variant={base.builtin ? 'primary' : 'secondary'} size="sm" onClick={() => { setName(base.builtin ? '' : `${base.name} (copy)`); setNaming('new'); }}>Save as new view…</Button>}
                {!dirty && !base.builtin && <>
                  <Button type="button" variant="ghost" size="sm" onClick={() => { setName(base.name); setNaming('rename'); }}>Rename…</Button>
                  <Button type="button" variant="ghost" size="sm" onClick={() => setRemoving(true)}>Remove view…</Button>
                </>}
                {!dirty && base.builtin && <Button type="button" variant="ghost" size="sm" onClick={() => { setName(''); setNaming('new'); }}><Icon name="plus" />New view…</Button>}
              </div>
            </div>
            {panel === 'filter' && fields.data && (
              <FilterPanel fields={fields.data} users={users.data ?? []} filter={draft.filter} status={status}
                onChange={(filter) => change({ filter })} onStatus={(s) => set({ status: s === 'active' ? null : s, page: null })} />
            )}
            {panel === 'columns' && fields.data && draft.layout === 'list' && <ColumnsPanel fields={fields.data} columns={draft.columns} onChange={(columns) => change({ columns })} />}
            <ErrorBanner error={error} />
            {!fields.data ? <ListSkeleton label="Loading contacts" /> : draft.layout === 'board'
              ? <Board fields={fields.data} body={{ ...body, ...(draft.lifecycle ? { lifecycle: draft.lifecycle } : {}) }} onCleared={() => { setQ(''); setDraft(base); }} />
              : <List fields={fields.data} view={draft} body={body} page={page} perPage={perPage} onSort={(sort) => change({ sort })}
                  onPage={(p) => set({ page: p === 1 ? null : String(p) })} onPerPage={(n) => set({ per: n === 25 ? null : String(n), page: null })}
                  onCleared={() => { setQ(''); setDraft(base); set({ q: null, tag: null, status: null, page: null }); }} />}
          </div>
        </Tabs>
      </div>
      <ConfirmDialog open={!!naming} title={naming === 'rename' ? 'Rename this view' : 'Save as a new view'} confirmLabel={naming === 'rename' ? 'Rename' : 'Save view'} busy={busy}
        onConfirm={() => { if (name.trim()) void saveView(naming === 'rename' ? 'rename' : 'new'); }} onCancel={() => setNaming(null)}>
        <Field label="View name" required hint={naming === 'new' ? 'It keeps this filter, these columns, the sort, and list or board.' : undefined}>
          <input className={fieldClass} value={name} maxLength={80} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' && name.trim()) { e.preventDefault(); void saveView(naming === 'rename' ? 'rename' : 'new'); } }} />
        </Field>
      </ConfirmDialog>
      <ConfirmDialog open={removing} title={`Remove the view ${base.name}?`} confirmLabel="Remove view" tone="danger" busy={busy} onConfirm={() => void removeView()} onCancel={() => setRemoving(false)}>
        <p style={{ margin: 0 }}>It leaves your tabs. No contact changes.</p>
      </ConfirmDialog>
    </div>
  );
}

function SortControl({ fields, sort, onChange }: { fields: Fields | null; sort: Sort; onChange: (s: Sort) => void }) {
  const options = [{ key: 'name', label: 'Name' }, ...(fields?.columns.filter((c) => c.sortable) ?? [])];
  return (
    <div className="row sort-control" style={{ gap: 4, flexWrap: 'nowrap' }}>
      <label className="sr-only" htmlFor="contacts-sort">Sort by</label>
      <Icon name="sort" />
      <select id="contacts-sort" className={`${selectClass} h-9 w-auto`} value={sort.field} onChange={(e) => onChange({ ...sort, field: e.target.value })}>
        {options.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
      </select>
      <Button type="button" variant="ghost" size="sm" onClick={() => onChange({ ...sort, dir: sort.dir === 'asc' ? 'desc' : 'asc' })}
        aria-label={sort.dir === 'asc' ? 'Ascending; sort descending' : 'Descending; sort ascending'}>{sort.dir === 'asc' ? 'A→Z' : 'Z→A'}</Button>
    </div>
  );
}

// Conditions that must all hold, in the segment filter vocabulary.
function FilterPanel({ fields, users, filter, status, onChange, onStatus }: { fields: Fields; users: User[]; filter: Cond[]; status: string; onChange: (f: Cond[]) => void; onStatus: (s: string) => void }) {
  const byField = new Map(fields.filters.map((f) => [f.field, f]));
  const put = (i: number, c: Cond) => onChange(filter.map((x, j) => (j === i ? c : x)));
  return (
    <section id="contacts-filter" className={`${cardClass} card-pad stack filter-panel`} style={{ gap: 10 }} aria-labelledby="contacts-filter-h">
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <h2 id="contacts-filter-h" style={{ margin: 0, fontSize: 15 }}>Contacts where all of these hold</h2>
        <Field label="Status">
          <select className={`${selectClass} h-9 w-auto`} value={status} onChange={(e) => onStatus(e.target.value)}>
            <option value="active">Active</option><option value="archived">Archived</option><option value="merged">Merged</option>
          </select>
        </Field>
      </div>
      {filter.length === 0 && <p className="muted small" style={{ margin: 0 }}>No conditions yet: every contact in this view shows.</p>}
      <ol className="conditions">
        {filter.map((c, i) => {
          const f = byField.get(c.field);
          return (
            <li key={i} className="condition">
              <label className="sr-only" htmlFor={`cond-f-${i}`}>Field {i + 1}</label>
              <select id={`cond-f-${i}`} className={`${selectClass} h-9 w-auto`} value={c.field} onChange={(e) => { const nf = byField.get(e.target.value)!; put(i, { field: nf.field, op: nf.ops[0]!, value: defaultValue(nf.type) }); }}>
                {fields.filters.map((x) => <option key={x.field} value={x.field}>{x.label}</option>)}
              </select>
              <label className="sr-only" htmlFor={`cond-o-${i}`}>Condition {i + 1}</label>
              <select id={`cond-o-${i}`} className={`${selectClass} h-9 w-auto`} value={c.op} onChange={(e) => put(i, { ...c, op: e.target.value, value: NO_VALUE.has(e.target.value) ? undefined : c.value ?? defaultValue(f?.type ?? 'text') })}>
                {(f?.ops ?? [c.op]).map((o) => <option key={o} value={o}>{OP_LABEL[o] ?? o}</option>)}
              </select>
              {!NO_VALUE.has(c.op) && <ValueInput id={`cond-v-${i}`} label={`Value ${i + 1}`} type={f?.type ?? 'text'} options={f?.options} fields={fields} users={users} value={c.value} onChange={(value) => put(i, { ...c, value })} />}
              {(c.op === 'within_days' || c.op === 'older_than_days') && <span className="small muted">days{c.op === 'older_than_days' ? ' ago' : ''}</span>}
              <Button type="button" variant="ghost" size="sm" aria-label={`Remove condition ${i + 1}`} onClick={() => onChange(filter.filter((_, j) => j !== i))}><Icon name="x" /></Button>
            </li>
          );
        })}
      </ol>
      <div className="row">
        <Button type="button" variant="secondary" size="sm" onClick={() => onChange([...filter, { field: 'lifecycle_stage', op: 'eq', value: stageChoices(fields)[0]?.[0] ?? '' }])}><Icon name="plus" />Add a condition</Button>
        {filter.length > 0 && <Button type="button" variant="ghost" size="sm" onClick={() => onChange([])}>Clear conditions</Button>}
      </div>
    </section>
  );
}

function defaultValue(type: string): unknown {
  return type === 'boolean' ? true : type === 'days' ? 30 : type === 'number' ? 0 : type === 'review' ? 'needs_review' : type === 'deal_status' || type === 'lead_status' ? 'open' : '';
}

function ValueInput({ id, label, type, options, fields, users, value, onChange }: { id: string; label: string; type: string; options: unknown; fields: Fields; users: User[]; value: unknown; onChange: (v: unknown) => void }) {
  const choose = (opts: Array<[string, string]>) => (
    <><label className="sr-only" htmlFor={id}>{label}</label>
      <select id={id} className={`${selectClass} h-9 w-auto`} value={String(value ?? '')} onChange={(e) => onChange(e.target.value)}>
        <option value="" disabled>Choose…</option>
        {opts.map(([v, t]) => <option key={v} value={v}>{t}</option>)}
      </select></>
  );
  switch (type) {
    case 'stage': return choose(stageChoices(fields));
    case 'class': return choose(fields.classes.map((c) => [c.key, c.name]));
    case 'user': return choose(users.filter((u) => u.status === 'active').map((u) => [u.id, u.display_name]));
    case 'grade': return choose(fields.grades.map((g) => [g.key, gradeName(fields, g.key)]));
    case 'kind': return choose([['person', 'Person'], ['household', 'Household'], ['organization', 'Organization']]);
    case 'review': return choose([['needs_review', 'Needs review'], ['reviewed', 'Reviewed'], ['none', 'Not queued']]);
    case 'deal_status': return choose([['open', 'Open'], ['won', 'Won'], ['lost', 'Lost']]);
    case 'lead_status': return choose([['open', 'Open'], ['converted', 'Converted'], ['disqualified', 'Disqualified'], ['archived', 'Archived']]);
    case 'select': return choose((Array.isArray(options) ? options : []).map((o: any) => (typeof o === 'string' ? [o, o] : [String(o.value), String(o.label ?? o.value)])));
    case 'boolean':
      return <><label className="sr-only" htmlFor={id}>{label}</label>
        <select id={id} className={`${selectClass} h-9 w-auto`} value={value === false ? 'no' : 'yes'} onChange={(e) => onChange(e.target.value === 'yes')}><option value="yes">Yes</option><option value="no">No</option></select></>;
    case 'days': case 'number':
      return <><label className="sr-only" htmlFor={id}>{label}</label>
        <input id={id} className={`${fieldClass} h-9`} type="number" inputMode="numeric" min={0} style={{ width: 90 }} value={String(value ?? '')} onChange={(e) => onChange(e.target.value === '' ? '' : Number(e.target.value))} /></>;
    default:
      return <><label className="sr-only" htmlFor={id}>{label}</label>
        <input id={id} className={`${fieldClass} h-9`} style={{ width: 180 }} value={String(value ?? '')} onChange={(e) => onChange(e.target.value)} /></>;
  }
}

// Which columns show, and in what order. Name always comes first.
function ColumnsPanel({ fields, columns, onChange }: { fields: Fields; columns: string[]; onChange: (c: string[]) => void }) {
  const label = (k: string) => fields.columns.find((c) => c.key === k)?.label ?? k;
  const move = (i: number, by: number) => { const next = [...columns]; const [c] = next.splice(i, 1); next.splice(i + by, 0, c!); onChange(next); };
  const others = fields.columns.filter((c) => !columns.includes(c.key));
  return (
    <section id="contacts-columns" className={`${cardClass} card-pad columns-panel`} aria-labelledby="contacts-columns-h">
      <h2 id="contacts-columns-h" style={{ margin: '0 0 8px', fontSize: 15 }}>Columns</h2>
      <div className="columns-grid">
        <div>
          <h3 className="small muted" style={{ margin: '0 0 6px' }}>Showing, in order</h3>
          <ol className="column-list">
            <li><span className="grow">Name</span><span className="small faint">Always first</span></li>
            {columns.map((k, i) => (
              <li key={k}>
                <span className="grow">{label(k)}</span>
                <Button type="button" variant="ghost" size="sm" aria-label={`Move ${label(k)} up`} disabled={i === 0} onClick={() => move(i, -1)}>↑</Button>
                <Button type="button" variant="ghost" size="sm" aria-label={`Move ${label(k)} down`} disabled={i === columns.length - 1} onClick={() => move(i, 1)}>↓</Button>
                <Button type="button" variant="ghost" size="sm" aria-label={`Hide ${label(k)}`} onClick={() => onChange(columns.filter((c) => c !== k))}><Icon name="x" /></Button>
              </li>
            ))}
          </ol>
        </div>
        <div>
          <h3 className="small muted" style={{ margin: '0 0 6px' }}>Add a column</h3>
          {others.length === 0 ? <p className="small muted" style={{ margin: 0 }}>Every column is showing.</p> : (
            <ul className="column-list">
              {others.map((c) => <li key={c.key}><span className="grow">{c.label}</span><Button type="button" variant="ghost" size="sm" aria-label={`Show ${c.label}`} disabled={columns.length >= 20} onClick={() => onChange([...columns, c.key])}><Icon name="plus" /></Button></li>)}
            </ul>
          )}
        </div>
      </div>
    </section>
  );
}

function List({ fields, view, body, page, perPage, onSort, onPage, onPerPage, onCleared }: {
  fields: Fields; view: View; body: Record<string, unknown>; page: number; perPage: number;
  onSort: (s: Sort) => void; onPage: (p: number) => void; onPerPage: (n: number) => void; onCleared: () => void;
}) {
  const [data, setData] = useState<{ rows: Row[]; total: number } | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const key = JSON.stringify([body, view.columns, view.sort, page, perPage]);
  useEffect(() => {
    let live = true;
    setBusy(true);
    api<{ rows: Row[]; total: number }>('/v1/parties/query', { body: { ...body, columns: view.columns, sort: view.sort, page, per_page: perPage } })
      .then((r) => { if (live) { setData(r); setError(null); } })
      .catch((e) => live && setError(e))
      .finally(() => live && setBusy(false));
    return () => { live = false; };
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
  const cols = view.columns.map((k) => fields.columns.find((c) => c.key === k) ?? { key: k, label: k, type: 'text', sortable: false });
  const pages = data ? Math.max(Math.ceil(data.total / perPage), 1) : 1;
  const sortBy = (field: string) => onSort({ field, dir: view.sort.field === field && view.sort.dir === 'asc' ? 'desc' : 'asc' });
  const ariaSort = (field: string) => (view.sort.field === field ? (view.sort.dir === 'asc' ? 'ascending' : 'descending') : undefined);
  const filtered = !!(body.q || (body.filter_tree as any).all.length || body.status !== 'active');
  const countText = data ? `${data.total.toLocaleString()} contact${data.total === 1 ? '' : 's'}` : '';
  return (
    <div className="stack" style={{ gap: 10 }}>
      <ErrorBanner error={error} />
      <div className={cardClass}>
        {!data ? (error ? null : <ListSkeleton label="Loading contacts" />) : data.rows.length === 0 ? (
          <Empty title={filtered ? 'No matches' : 'No contacts yet'} action={filtered
            ? <Button type="submit" variant="secondary" size="sm" onClick={onCleared}>Clear search and filters</Button>
            : undefined}>
            {filtered ? 'Try a different name, email, or phone, or change the filter.' : 'Import your book or add a contact.'}
          </Empty>
        ) : (
          <Table label={`Contacts in ${view.name}`} aria-busy={busy} tableClassName="contacts-table">
              <caption className="sr-only">Contacts in {view.name}, page {page} of {pages}</caption>
              <thead><tr>
                <th scope="col" aria-sort={ariaSort('name')}><button type="button" className="th-sort" onClick={() => sortBy('name')}>Name<SortMark on={view.sort.field === 'name'} dir={view.sort.dir} /></button></th>
                {cols.map((c) => (
                  <th key={c.key} scope="col" aria-sort={c.sortable ? ariaSort(c.key) : undefined}>
                    {c.sortable ? <button type="button" className="th-sort" onClick={() => sortBy(c.key)}>{c.label}<SortMark on={view.sort.field === c.key} dir={view.sort.dir} /></button> : c.label}
                  </th>
                ))}
              </tr></thead>
              <tbody>
                {data.rows.map((r) => (
                  <tr key={r.id}>
                    <th scope="row" className="contact-name"><Link to={`/contacts/${r.id}`}><Avatar name={r.display_name} kind={r.kind} /><span>{r.display_name}</span></Link></th>
                    {cols.map((c) => <td key={c.key}><Cell type={c.type} value={r.values[c.key]} row={r} fields={fields} /></td>)}
                  </tr>
                ))}
              </tbody>
            </Table>
        )}
      </div>
      {data && data.total > 0 && (
        <div className="pager">
          <span className="faint small" role="status" aria-live="polite">{countText}</span>
          <Pagination label="Pages" page={page} pages={pages} onPageChange={onPage} />
          <label className="row small" style={{ gap: 6 }}>Per page
            <select className={`${selectClass} h-9 w-auto`} value={perPage} onChange={(e) => onPerPage(Number(e.target.value))}><option>25</option><option>50</option><option>100</option></select>
          </label>
        </div>
      )}
    </div>
  );
}

const SortMark = ({ on, dir }: { on: boolean; dir: string }) => <span className="sort-mark" aria-hidden="true">{on ? (dir === 'asc' ? '▲' : '▼') : ''}</span>;

function Cell({ type, value, row, fields }: { type: string; value: any; row: Row; fields: Fields }) {
  if (value === null || value === undefined || value === '' || (Array.isArray(value) && !value.length)) return <span className="faint">–</span>;
  switch (type) {
    case 'stage': return <Badge>{stageName(fields, row.contact_class, value)}</Badge>;
    case 'class': return <>{className(fields, value)}</>;
    case 'grade': return <span title={gradeName(fields, value)}>{value}</span>;
    case 'date': return <Time iso={value} />;
    case 'tags': return <span className="row" style={{ gap: 4 }}>{(value as string[]).slice(0, 3).map((t) => <span key={t} className="tag">{t}</span>)}</span>;
    case 'party': return value.name === HIDDEN ? <span className="muted">{HIDDEN}</span> : <Link to={`/contacts/${value.id}`}>{value.name}</Link>;
    case 'email': return <span className="nowrap">{value}</span>;
    case 'number': return <span className="num-cell">{Number(value).toLocaleString()}</span>;
    case 'text': return <>{value === 'person' ? 'Person' : value === 'organization' ? 'Organization' : value === 'household' ? 'Household' : String(value)}</>;
    default: return <>{String(value)}</>;
  }
}

// The board: a column per stage of one lifecycle, on the card board. A move is a
// party.set_stage proposal, a new owner a party.update; the board reloads to show the gate's answer.
const NONE = 'none';
function Board({ fields, body, onCleared }: { fields: Fields; body: Record<string, unknown>; onCleared: () => void }) {
  const toast = useToast();
  const { me } = useSession();
  const users = useApi<User[]>('/v1/users');
  const [cols, setCols] = useState<Column[] | null>(null);
  const [lifecycle, setLifecycle] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState<ReadonlySet<string>>(new Set());
  const [local, setLocal] = useState<KanbanCard[] | null>(null);
  const [reloads, setReloads] = useState(0);
  const key = JSON.stringify(body);
  useEffect(() => {
    let live = true;
    api<{ lifecycle: string; columns: Column[] }>('/v1/parties/board', { body })
      .then((r) => { if (live) { setCols(r.columns); setLifecycle(r.lifecycle); setError(null); setLocal(null); } })
      .catch((e) => live && setError(e));
    return () => { live = false; };
  }, [key, reloads]); // eslint-disable-line react-hooks/exhaustive-deps

  const contacts = useMemo(() => new Map(cols?.flatMap((c) => c.cards).map((c) => [c.id, c]) ?? []), [cols]);
  const fromServer = useMemo(() => cols?.flatMap((col) => col.cards.map((c): KanbanCard => ({
    id: c.id, title: c.display_name, status: c.lifecycle_stage ?? NONE, assignee: c.owner_user_id, subtitle: c.email ?? undefined, href: `/contacts/${c.id}`,
  }))) ?? [], [cols]);
  const cards = (local ?? fromServer).map((c) => (busy.has(c.id) ? { ...c, busy: true } : c));
  const people = useMemo(() => {
    const list = (users.data ?? []).filter((u) => u.status === 'active').map((u) => ({ id: u.id, name: u.display_name }));
    for (const c of contacts.values()) if (c.owner_user_id && !list.some((p) => p.id === c.owner_user_id)) list.push({ id: c.owner_user_id, name: c.owner_name ?? 'Someone' });
    return list;
  }, [users.data, contacts]);

  async function apply(next: KanbanCard[]) {
    const changes = changedCards(local ?? fromServer, next);
    setLocal(next);
    const ids = changes.map((c) => c.card.id);
    setBusy((b) => new Set([...b, ...ids]));
    try {
      for (const { card, was } of changes) {
        const c = contacts.get(card.id);
        if (!c || !was) continue;
        let version = c.version;
        if (card.status !== was.status) {
          const stage = card.status === NONE ? null : card.status;
          const out = await propose('party.set_stage', { party_id: c.id, base_version: version, stage });
          toast(out.state === 'written' ? `Moved ${c.display_name} to ${stageName(fields, c.contact_class, stage)}` : outcomeMessage(out), outcomeTone(out));
          if (out.state === 'written') version++;
        }
        if (card.assignee !== was.assignee) {
          const out = await propose('party.update', { party_id: c.id, base_version: version, set: { owner_user_id: card.assignee } });
          toast(outcomeMessage(out, `Saved ${c.display_name}`), outcomeTone(out));
        }
      }
    } catch (e) { setError(e); }
    setBusy((b) => new Set([...b].filter((id) => !ids.includes(id))));
    setReloads((n) => n + 1);
  }
  async function more(stage: string) {
    const col = cols?.find((c) => (c.stage ?? NONE) === stage);
    if (!col) return;
    try {
      const r = await api<{ columns: Column[] }>('/v1/parties/board', { body: { ...body, only: true, stage: col.stage, offset: col.cards.length } });
      setCols((cs) => cs?.map((c) => (c.stage === col.stage ? { ...c, cards: [...c.cards, ...r.columns[0]!.cards] } : c)) ?? null);
    } catch (e) { setError(e); }
  }
  const total = cols?.reduce((n, c) => n + c.count, 0) ?? 0;
  const lc = fields.lifecycles.find((l) => l.key === lifecycle);
  const colName = (stage: string | null) => (stage ? lc?.stages.find((s) => s.key === stage)?.name ?? stage : NO_STAGE);
  const by = lc && fields.lifecycles.length > 1 ? `lifecycle stage: ${lc.name}` : 'lifecycle stage';
  if (error && !cols) return <ErrorBanner error={error} />;
  if (!cols) return <ListSkeleton label="Loading the board" />;
  if (!total) return <div className={cardClass}><Empty title="No matches" action={<Button type="submit" variant="secondary" size="sm" onClick={onCleared}>Clear search and filters</Button>}>No contact in this view matches.</Empty></div>;
  return (
    <div className="stack" style={{ gap: 8 }}>
      <ErrorBanner error={error} />
      <p className="faint small" role="status" aria-live="polite" style={{ margin: 0 }}>{total.toLocaleString()} contact{total === 1 ? '' : 's'} by {by}</p>
      <CardBoard title={`Contacts by ${by}`} names={{ status: 'Stage', assignee: 'Owner' }} fixed={['add', 'title', 'description']}
        columns={cols.map((c) => ({ id: c.stage ?? NONE, title: colName(c.stage), total: c.count }))}
        people={people} currentUser={me.user_id} cards={cards} onCardsChange={(next) => void apply(next)} onLoadMore={(s) => void more(s)} />
    </div>
  );
}
